import { Link } from 'react-router-dom';
import type { WellnessDailyStatus } from '../../../shared/types';
import type { Resource } from '../useResource';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../ui';

/**
 * The day's wellness state on the hub. Adults only — /api/wellness is adult-gated and
 * 'wellness' is in ADULT_ONLY_CARDS, so a child can neither choose this card nor read it.
 *
 * NOT the same thing as HealthCard, which is the household BUDGET score. Two different
 * cards, two different meanings of "health", which is exactly why this module is called
 * wellness.
 *
 * ONE DOT PER EXPECTED THING, and the dots are DERIVED from `/today` — from each metric's
 * daily_expected and from the member's own medication list — never from a hardcoded five.
 * Adding a supplement changes this card with no redeploy.
 *
 * Reads /api/wellness/today and nothing else: one call, per the PRD.
 */
export default function WellnessCard({ res }: { res: Resource<WellnessDailyStatus> }) {
  const { data, loading, error, reload } = res;

  return (
    <Card className="flex flex-col p-5 sm:p-6">
      <CardTitle icon="pulse" action={{ to: '/wellness', label: 'Open' }}>
        Wellness
      </CardTitle>

      <div className="mt-4 flex-1">
        {loading && <LoadingRows rows={2} />}

        {!loading && error && <ErrorState what="today’s wellness" onRetry={reload} />}

        {!loading && !error && data && data.items.length === 0 && (
          <Empty
            message="Nothing set up to track yet."
            action={{ to: '/wellness', label: 'Start logging' }}
          />
        )}

        {!loading && !error && data && data.items.length > 0 && <Dots status={data} />}
      </div>
    </Card>
  );
}

function Dots({ status }: { status: WellnessDailyStatus }) {
  const remaining = status.items.filter((i) => i.done < i.expected);

  return (
    <div>
      <ul className="flex flex-wrap gap-2">
        {status.items.map((i) => {
          const done = i.done >= i.expected;
          return (
            <li key={i.key}>
              {/* Never colour alone: the dot carries a check, and the list below names
                  anything outstanding in words. */}
              <span
                title={`${i.label}: ${i.done} of ${i.expected}`}
                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold ${
                  done ? 'bg-sage text-white' : 'border border-border bg-canvas text-ink-faint'
                }`}
              >
                {done ? (
                  <Icon name="check" className="h-4 w-4" />
                ) : i.expected > 1 ? (
                  `${i.done}/${i.expected}`
                ) : (
                  ''
                )}
              </span>
              <span className="sr-only">
                {i.label}: {i.done} of {i.expected} logged
              </span>
            </li>
          );
        })}
      </ul>

      {/* Factual. No streak, no praise, no warning — the module's tone rule. */}
      <p className="mt-3 text-sm text-ink-soft">
        {remaining.length === 0
          ? 'Everything logged today.'
          : `Still to log: ${remaining.map((i) => i.label).join(', ')}.`}
      </p>

      <Link
        to="/wellness"
        className="mt-3 inline-flex min-h-11 items-center rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        Log something
      </Link>
    </div>
  );
}
